/**
 * SpaceItemsService — Firestore I/O for the private life-space item collections
 * (personalItems / ownersItems / barbaraItems / captureItems).
 *
 * OWNER-ONLY BY RULE. All four are gated on isOwner() in firestore.rules — that
 * is `users/{uid}.owner == true`, NOT isAdmin(), because Grace is an `employee`.
 * The client-side space guard in SpaceShell is courtesy; this is the wall. A
 * non-owner subscribing here gets a permission-denied on the snapshot, which is
 * the correct outcome and is why onError is a required argument rather than an
 * option — a denial must reach the UI, not vanish into a console.
 *
 * NO orderBy, DELIBERATELY. The query is an unordered collection read capped at
 * `max`, sorted in memory by spaceItemsModel.sortItems(). Ordering in Firestore
 * would mean a composite index (and therefore an index deploy) to buy nothing:
 * these collections are a household to-do list, currently 10 / 4 / 2 / 2
 * documents. If one ever grows past the cap, the fix is a real query with a real
 * index, not a bigger number.
 *
 * READ-ONLY IN THIS SLICE. Create/update/delete are deliberately absent — the
 * Personal Tasks surface decision (reuse the Work task module vs. a thin
 * list) has not been made, and writing a create path now would pre-commit it.
 */

import { collection, onSnapshot, query, limit as fsLimit } from 'firebase/firestore';
import { db } from '../firebase';
import { normalizeItem } from './spaceItemsModel';

/** Collections this service is allowed to touch — all four are owner-walled. */
export const SPACE_ITEM_COLLECTIONS = [
	'personalItems',
	'ownersItems',
	'barbaraItems',
	'captureItems',
];

/**
 * Live subscription to one space-item collection.
 *
 * @param {string} collectionName one of SPACE_ITEM_COLLECTIONS
 * @param {(items: object[]) => void} onData normalized items, unsorted
 * @param {(err: Error) => void} onError snapshot errors, including permission-denied
 * @param {{max?: number}} [opts]
 * @returns {() => void} unsubscribe
 */
export function subscribeToSpaceItems(collectionName, onData, onError, { max = 500 } = {}) {
	if (!SPACE_ITEM_COLLECTIONS.includes(collectionName)) {
		// A typo here would hit the rules' default-deny catch-all and surface as a
		// generic permission error, which is a miserable thing to debug. Fail loudly.
		const err = new Error(`Unknown space item collection: ${collectionName}`);
		onError?.(err);
		return () => {};
	}

	const q = query(collection(db, collectionName), fsLimit(max));

	return onSnapshot(
		q,
		(snap) => onData(snap.docs.map((d) => normalizeItem(d.data(), d.id))),
		(err) => onError?.(err)
	);
}
